/**
 * Created by Jie Gao on 2017-05-24.
 */

import {Currency} from "@nolimitcity/slot-game/bin/core/balance/Currency";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {
    IAnimationFrameConfig,
    IBounceAnimation,
    IBounceAnimationConfig,
    IPosition,
    IWiggleConfig
} from "../../animation/IAnimationConfig";
import {AnimationHelper} from "../../utils/AnimationHelper";
import {WixxGameConfig} from "../../WixxGameConfig";
import {BigWinNonLoopAnimationContainer} from "./BigWinNonLoopAnimationContainer";
import {BigWinWhiteBarAnimation} from "./BigWinWhiteBarAnimation";

export interface IWinAmountConfig {
    width?:number;
    height:number;
    centerX?:number;
    centerY:number;
    winNumberStyle:TextStyleOptions;
}

export interface IBounceAnimationTexture {
    left:string;
    right:string;
    center?:string;
    blendMode?:number;
}

export interface IBounceAnimationSprite {
    left?:PIXI.Sprite;
    right?:PIXI.Sprite;
    center?:PIXI.Sprite;
}

export class WixxBigWinView extends BaseView {
    private readonly _FRAME_SPEED:number = 1 / 30;
    private readonly _TIME_CONFIG:number[] = [0.545, 7.8, 14.44, 21.4, 27.73];
    private readonly _BURST_POS_CONFIG:IPosition = {x : GameConfig.instance.GAME_WIDTH / 2, y : 278};
    private readonly _WIN_AMOUNT_CONFIG:IWinAmountConfig = {
        width : 700,
        height : 85,
        centerY : GameConfig.instance.GAME_WIDTH / 2,
        winNumberStyle : {
            fontFamily : "Open Sans",
            fontSize : 40,
            fontWeight : "800",
            fill : "#ffffff"
        }
    };
    private readonly _WIGGLE_ANIMATION_CONFIG:IWiggleConfig[] = [
        {speed : 1.9, offset : 20},
        {speed : 1.7, offset : 30},
        {speed : 1.5, offset : 40},
        {speed : 1.3, offset : 50}
    ];
    private readonly _ANIMATION_TEXTURES:IBounceAnimationTexture[] = [
        {
            left : "bigWin_bigWin_left",
            right : "bigWin_bigWin_right",
            blendMode : PIXI.BLEND_MODES.ADD
        },
        {
            left : "bigWin_megaWin_left",
            right : "bigWin_megaWin_right",
            blendMode : PIXI.BLEND_MODES.ADD
        },
        {
            left : "bigWin_superMegaWin_left",
            right : "bigWin_superMegaWin_right",
            blendMode : PIXI.BLEND_MODES.ADD
        },
        {
            left : "bigWin_extremeWin_left",
            center : "bigWin_extremeWin_center",
            right : "bigWin_extremeWin_right",
            blendMode : PIXI.BLEND_MODES.ADD
        }
    ];
    private readonly _ANIMATION_FRAMES:IBounceAnimationConfig[] = [
        {
            left : {
                bounceIn : [
                    {frame : 0, x : 50 + 72, y : 725 - 12, alpha : 1},
                    {frame : 1, x : 50 + 72, y : 725 - 12, alpha : 1},
                    {frame : 14, x : 483 + 72, y : 291 - 12, alpha : 1},
                    {frame : 17, x : 452 + 72, y : 323 - 12, alpha : 1}],
                stay : {frame : 0, x : 452 + 72, y : 323 - 12, alpha : 1},
                bounceOut : [
                    {frame : 104, x : 452 + 72, y : 323 - 12, alpha : 1},
                    {frame : 113, x : 430 + 72, y : 344 - 12, alpha : 1},
                    {frame : 118, x : 860 + 72, y : -85 - 12, alpha : 1},
                    {frame : 119, x : 860 + 72, y : -85 - 12, alpha : 0}]
            },
            right : {
                bounceIn : [
                    {frame : 0, x : 1112 + 72, y : -87 - 12, alpha : 1},
                    {frame : 1, x : 1112 + 72, y : -87 - 12, alpha : 1},
                    {frame : 14, x : 661 + 72, y : 365 - 12, alpha : 1},
                    {frame : 17, x : 701 + 72, y : 325 - 12, alpha : 1}],
                stay : {frame : 0, x : 701 + 72, y : 325 - 12, alpha : 1},
                bounceOut : [
                    {frame : 104, x : 701 + 72, y : 325 - 12, alpha : 1},
                    {frame : 113, x : 741 + 72, y : 284 - 12, alpha : 1},
                    {frame : 118, x : 303 + 72, y : 724 - 12, alpha : 1}]
            }
        },
        {
            left : {
                bounceIn : [
                    {frame : 0, x : 32 + 72, y : -99.5 - 12, alpha : 1},
                    {frame : 1, x : 32 + 72, y : -99.5 - 12, alpha : 1},
                    {frame : 6, x : 466 + 72, y : 335.5 - 12, alpha : 1},
                    {frame : 9, x : 442 + 72, y : 310.5 - 12, alpha : 1}],
                stay : {frame : 0, x : 442 + 72, y : 310.5 - 12, alpha : 1},
                bounceOut : [
                    {frame : 108, x : 442 + 72, y : 310.5 - 12, alpha : 1},
                    {frame : 111, x : 417 + 72, y : 286.5 - 12, alpha : 1},
                    {frame : 117, x : 854 + 72, y : 726.5 - 12, alpha : 1}]
            },
            right : {
                bounceIn : [
                    {frame : 0, x : 1063.25 + 72, y : 740.75 - 12, alpha : 1},
                    {frame : 1, x : 1063.25 + 72, y : 740.75 - 12, alpha : 1},
                    {frame : 6, x : 604.25 + 72, y : 282.75 - 12, alpha : 1},
                    {frame : 9, x : 621.25 + 72, y : 298.75 - 12, alpha : 1}],
                stay : {frame : 0, x : 621.25 + 72, y : 298.75 - 12, alpha : 1},
                bounceOut : [
                    {frame : 108, x : 621.25 + 72, y : 298.75 - 12, alpha : 1},
                    {frame : 111, x : 662.25 + 72, y : 339.75 - 12, alpha : 1},
                    {frame : 117, x : 241.25 + 72, y : -78.25 - 12, alpha : 1}]
            }
        },
        {
            left : {
                bounceIn : [
                    {frame : 0, x : 18 + 72, y : 742.5 - 12, alpha : 1},
                    {frame : 1, x : 18 + 72, y : 742.5 - 12, alpha : 1},
                    {frame : 7, x : 507 + 72, y : 256.5 - 12, alpha : 1},
                    {frame : 17, x : 440 + 72, y : 322.5 - 12, alpha : 1}],
                stay : {frame : 0, x : 440 + 72, y : 322.5 - 12, alpha : 1},
                bounceOut : [
                    {frame : 99, x : 440 + 72, y : 322.5 - 12, alpha : 1},
                    {frame : 108, x : 415 + 72, y : 345.5 - 12, alpha : 1},
                    {frame : 113, x : 876 + 72, y : -113.5 - 12, alpha : 1}
                ]
            },
            right : {
                bounceIn : [
                    {frame : 0, x : 1064 + 72, y : -102.5 - 12, alpha : 1},
                    {frame : 1, x : 1064 + 72, y : -102.5 - 12, alpha : 1},
                    {frame : 7, x : 593 + 72, y : 368.5 - 12, alpha : 1},
                    {frame : 17, x : 639 + 72, y : 321.5 - 12, alpha : 1}],
                stay : {frame : 0, x : 639 + 72, y : 321.5 - 12, alpha : 1},
                bounceOut : [
                    {frame : 99, x : 639 + 72, y : 321.5 - 12, alpha : 1},
                    {frame : 107, x : 658 + 72, y : 303.5 - 12, alpha : 1},
                    {frame : 113, x : 218 + 72, y : 744.5 - 12, alpha : 1}
                ]
            }
        },
        {
            left : {
                bounceIn : [
                    {frame : 0, x : -25 + 72, y : -139.5 - 12, alpha : 1},
                    {frame : 1, x : -25 + 72, y : -139.5 - 12, alpha : 1},
                    {frame : 6, x : 440 + 72, y : 336.5 - 12, alpha : 1},
                    {frame : 12, x : 353.5 + 72, y : 242 - 12, alpha : 1}],
                stay : {frame : 0, x : 353.5 + 72, y : 242 - 12, alpha : 1}
            },
            center : {
                bounceIn : [
                    {frame : 0, x : 582 + 72, y : -140.5 - 12, alpha : 1},
                    {frame : 1, x : 582 + 72, y : -140.5 - 12, alpha : 1},
                    {frame : 11, x : 582 + 72, y : 286.5 - 12, alpha : 1}],
                stay : {frame : 0, x : 582 + 72, y : 286.5 - 12, alpha : 1}
            },
            right : {
                bounceIn : [
                    {frame : 0, x : 995 + 72, y : -139.5 - 12, alpha : 1},
                    {frame : 1, x : 995 + 72, y : -139.5 - 12, alpha : 1},
                    {frame : 6, x : 680 + 72, y : 356.5 - 12, alpha : 1},
                    {frame : 12, x : 797.5 + 72, y : 264.5 - 12, alpha : 1}],
                stay : {frame : 113, x : 797.5 + 72, y : 264.5 - 12, alpha : 1}
            }
        }
    ];
    private readonly _END_ANIMATION_FRAMES:IAnimationFrameConfig[][] = [
        [
            {frame : 0, alpha : 1},
            {frame : 2, alpha : 1},
            {frame : 6, alpha : 1},
            {frame : 31, alpha : 1},
            {frame : 42, alpha : 1},
            {frame : 43, alpha : 0.1},
            {frame : 44, alpha : 0.7},
            {frame : 46, alpha : 0},
            {frame : 47, alpha : 1},
            {frame : 51, alpha : 0},
            {frame : 53, alpha : 1},
            {frame : 54, alpha : 0},
            {frame : 55, alpha : 1},
            {frame : 56, alpha : 0},
            {frame : 57, alpha : 1},
            {frame : 61, alpha : 0},
            {frame : 63, alpha : 1},
            {frame : 64, alpha : 0},
            {frame : 65, alpha : 1},
            {frame : 66, alpha : 0}
        ], [
            {frame : 0, alpha : 1},
            {frame : 4, alpha : 1},
            {frame : 12, alpha : 1},
            {frame : 31, alpha : 1},
            {frame : 44, alpha : 1},
            {frame : 45, alpha : 0.1},
            {frame : 46, alpha : 0.7},
            {frame : 48, alpha : 0},
            {frame : 49, alpha : 1},
            {frame : 53, alpha : 0},
            {frame : 55, alpha : 1},
            {frame : 56, alpha : 0},
            {frame : 57, alpha : 1},
            {frame : 58, alpha : 0},
            {frame : 59, alpha : 1},
            {frame : 68, alpha : 0}
        ], [
            {frame : 0, alpha : 1},
            {frame : 5, alpha : 1},
            {frame : 10, alpha : 1},
            {frame : 25, alpha : 1},
            {frame : 41, alpha : 1},
            {frame : 42, alpha : 0.1},
            {frame : 43, alpha : 0.7},
            {frame : 45, alpha : 0},
            {frame : 46, alpha : 1},
            {frame : 50, alpha : 0},
            {frame : 52, alpha : 1},
            {frame : 53, alpha : 0},
            {frame : 54, alpha : 1},
            {frame : 56, alpha : 1},
            {frame : 60, alpha : 0},
            {frame : 62, alpha : 1},
            {frame : 63, alpha : 0},
            {frame : 64, alpha : 1},
            {frame : 65, alpha : 0},
            {frame : 65, alpha : 0}
        ]
    ];
    private readonly _END_SHORT_ANIMATION_FRAMES:IAnimationFrameConfig[][] = [
        [
            {frame : 0, alpha : 1},
            {frame : 2, alpha : 1},
            {frame : 6, alpha : 1},
            {frame : 30, alpha : 1}
        ], [
            {frame : 0, alpha : 1},
            {frame : 4, alpha : 1},
            {frame : 12, alpha : 1},
            {frame : 30, alpha : 1}
        ], [
            {frame : 0, alpha : 1},
            {frame : 5, alpha : 1},
            {frame : 10, alpha : 1},
            {frame : 25, alpha : 1},
            {frame : 30, alpha : 1}
        ]
    ];

    private _winLevel:number;
    private _gameMode:string;
    private _currentLevel:number;

    private _layer:ScalableLayer;
    private _whiteBarAnimation:BigWinWhiteBarAnimation;
    private _nonStopAnimationContainer:BigWinNonLoopAnimationContainer;
    private _burst:TimelineSprite;

    private _animationSprites:IBounceAnimationSprite;
    private _animationOverlaySprites:IBounceAnimationSprite;
    //win amount
    private _wixxAnimationContainer:PIXI.Container;
    private _winNumber:PIXI.Text;
    private _singleWin:number;

    public _masterTimeLine:TimelineLite | null;

    constructor() {
        super();

        this._layer = StageManager.getLayer(WixxGameConfig.instance.LAYERS.BIG_WIN.name);
        this._whiteBarAnimation = new BigWinWhiteBarAnimation(this._layer);
        this._nonStopAnimationContainer = new BigWinNonLoopAnimationContainer(this._layer);
    }

    protected initAnimations():void {
        this._winLevel = -1;
        this._currentLevel = -1;
        this._gameMode = "BASIC";
        this._wixxAnimationContainer = new PIXI.Container();
        this._nonStopAnimationContainer.initAnimations();
        this._animationSprites = this.createSprites();
        this._animationOverlaySprites = this.createSprites();

        this._burst = AnimationHelper.createTimelineSprite(GameResources.getTextures("winBurst"));
        this._burst.blendMode = PIXI.BLEND_MODES.ADD;
        this._burst.x = this._BURST_POS_CONFIG.x;
        this._burst.y = this._BURST_POS_CONFIG.y;
        this._burst.scale.x = 2;
        this._burst.scale.y = 2;
        this._layer.addChild(this._burst);
        this._burst.hide();

        this._winNumber = this.createWinText(this._WIN_AMOUNT_CONFIG.winNumberStyle);
    }

    private createSprites():IBounceAnimationSprite {
        let bounceAnimationSprite:IBounceAnimationSprite = {};
        bounceAnimationSprite.left = this.createEmptySprite();
        bounceAnimationSprite.right = this.createEmptySprite();
        bounceAnimationSprite.center = this.createEmptySprite();
        return bounceAnimationSprite;
    }

    private createEmptySprite():PIXI.Sprite {
        const sprite = new PIXI.Sprite();
        sprite.x = 0;
        sprite.y = 0;
        sprite.anchor.set(0.5);
        this._wixxAnimationContainer.addChild(sprite);
        sprite.alpha = 0;
        return sprite;
    }

    private createWinText(style:TextStyleOptions):PIXI.Text {
        let text:PIXI.Text = new PIXI.Text("", style);
        text.anchor.set(0.5);
        text.y = 460;
        text.alpha = 0;
        return text;
    }

    public startPresentation(singleWin:number, winLevel:number, gameMode:string):void {
        this._singleWin = singleWin;
        this._winLevel = winLevel;
        this._gameMode = gameMode;
        this._layer.addChild(this._wixxAnimationContainer);
        this._nonStopAnimationContainer.startAnimation();
    }

    public getWinLevelAnimation(level:number):Timeline {
        this._masterTimeLine = new TimelineLite();
        this._currentLevel = level;
        const frames = this._ANIMATION_FRAMES[level];
        const textures = this._ANIMATION_TEXTURES[level];
        const wiggleOffsets:number[][] = [];
        const wiggleSpeed = this._WIGGLE_ANIMATION_CONFIG[level].speed;
        const wiggleOffset = this._WIGGLE_ANIMATION_CONFIG[level].offset;
        let me = this;
        let duration:number = 0.7;
        let delay:number = 0;
        let bounceInDuration:number = 0;
        let bounceOutDuration:number = 0;

        if(this._TIME_CONFIG[level + 1] && this._TIME_CONFIG[level]) {
            duration = this._TIME_CONFIG[level + 1] - this._TIME_CONFIG[level];
        }
        if(frames.left) {
            let bounceOut:IAnimationFrameConfig[] = frames.left.bounceOut!;
            bounceInDuration = frames.left.bounceIn[frames.left.bounceIn.length - 1].frame * me._FRAME_SPEED;
            bounceOutDuration = bounceOut ? (bounceOut[bounceOut.length - 1].frame - bounceOut[0].frame) * me._FRAME_SPEED : 0;

            while((duration - bounceOutDuration - bounceInDuration) > delay) {
                wiggleOffsets.push([(Math.random() - 0.5) * wiggleOffset!, (Math.random() - 0.5) * wiggleOffset!]);
                delay += wiggleSpeed * this._FRAME_SPEED;
            }
        }

        this._whiteBarAnimation.startPresentation(level);

        const burstTimeline = new TimelineLite();
        burstTimeline.add(() => {
            this._burst.show();
            this._burst.alpha = 1;
        });
        burstTimeline.add(this._burst.getAnimation());
        this._masterTimeLine.add(burstTimeline, (bounceInDuration - 3 * this._FRAME_SPEED).toString());

        if(frames.left) {
            this._animationSprites.left!.texture = GameResources.getTextures(textures.left)[0];
            this._animationOverlaySprites.left!.texture = GameResources.getTextures(textures.left)[0];
            this._animationOverlaySprites.left!.blendMode = textures.blendMode!;
            this._masterTimeLine.add(this.getWinAnimation(this._animationSprites.left!, this._animationOverlaySprites.left!, wiggleOffsets, frames.left, duration), '0');
            this._masterTimeLine.add(new TweenLite(this._animationSprites.left!, 0.1, {alpha : 1}), '0');
        }
        if(frames.right) {
            this._animationSprites.right!.texture = GameResources.getTextures(textures.right)[0];
            this._animationOverlaySprites.right!.texture = GameResources.getTextures(textures.right)[0];
            this._animationOverlaySprites.right!.blendMode = textures.blendMode!;
            this._masterTimeLine.add(this.getWinAnimation(this._animationSprites.right!, this._animationOverlaySprites.right!, wiggleOffsets, frames.right, duration), '0');
            this._masterTimeLine.add(new TweenLite(this._animationSprites.right!, 0.1, {alpha : 1}), '0');
        }
        if(frames.center) {
            this._animationSprites.center!.texture = GameResources.getTextures(textures.center!)[0];
            this._animationOverlaySprites.center!.texture = GameResources.getTextures(textures.center!)[0];
            this._animationOverlaySprites.center!.blendMode = textures.blendMode!;
            this._masterTimeLine.add(this.getWinAnimation(this._animationSprites.center!, this._animationOverlaySprites.center!, wiggleOffsets, frames.center, duration), '0');
            this._masterTimeLine.add(new TweenLite(this._animationSprites.center!, 0.1, {alpha : 1}), '0');
        }
        this._nonStopAnimationContainer.animateNeon(duration);
        this._masterTimeLine.eventCallback("onComplete", function() {
            me._whiteBarAnimation.stopAnimation();
        });

        return this._masterTimeLine;
    }

    private getWinAnimation(sprite:PIXI.Sprite, overlaySprite:PIXI.Sprite, offsets:number[][], frames:IBounceAnimation, totalDuration:number):TimelineLite {
        const timeline = new TimelineLite();
        const fs:number = this._FRAME_SPEED;
        const wiggleFrequancy:number = this._WIGGLE_ANIMATION_CONFIG[this._currentLevel].speed * fs;
        let me = this;
        let i:number = 0;
        let duration:number = 0;
        let bounceOut:IAnimationFrameConfig[] = frames.bounceOut!;
        let bounceInDuration = frames.bounceIn[frames.bounceIn.length - 1].frame * fs;
        let bounceOutDuration = bounceOut ? (bounceOut[bounceOut.length - 1].frame - bounceOut[0].frame) * fs : 0;

        if(frames.bounceIn) {
            for(i = 0; i < frames.bounceIn.length; i++) {
                duration = i > 0 ? (frames.bounceIn[i].frame - frames.bounceIn[i - 1].frame) * fs : 0;
                timeline.add(new TweenLite(sprite, duration, {
                    x : frames.bounceIn[i].x!,
                    y : frames.bounceIn[i].y!
                }));
            }
        }

        if(frames.stay) {
            timeline.add([new TweenLite(sprite, (totalDuration - bounceOutDuration - bounceInDuration), {
                x : frames.stay.x!,
                y : frames.stay.y!
            })]);
        }

        if(frames.bounceOut && (this._currentLevel !== this._winLevel)) {
            for(i = 0; i < frames.bounceOut.length; i++) {
                duration = i > 0 ? (frames.bounceOut[i].frame - frames.bounceOut[i - 1].frame) * fs : 0;
                timeline.add(new TweenLite(sprite, duration, {
                    x : frames.bounceOut[i].x!,
                    y : frames.bounceOut[i].y!
                }));
            }
        }

        const wiggleTimeline = new TimelineLite();
        for(let i:number = 0; i < (offsets.length - 1); i++) {
            wiggleTimeline.add(new TweenLite(overlaySprite, 0, {
                x : frames.stay.x! + offsets[i][0],
                y : frames.stay.y! + offsets[i][1],
                alpha : 0.3,
                delay : wiggleFrequancy
            }));
        }
        wiggleTimeline.eventCallback('onComplete', function() {
            me.removeOverlaySprites();
        });
        timeline.add(wiggleTimeline, (bounceInDuration - 3 * fs).toString());
        timeline.eventCallback('onComplete', function() {
            me.removeAnimationSprites();
        });
        return timeline;
    }

    public playEndAnimation(isAborted:boolean):void {
        this.showWinAmount();

        const sprites = this._animationSprites;
        const frames = this._ANIMATION_FRAMES[this._winLevel];
        const textures = this._ANIMATION_TEXTURES[this._winLevel];
        const timeline:TimelineLite = new TimelineLite();
        const fs:number = this._FRAME_SPEED;
        let frame:IAnimationFrameConfig;
        let i:number = 0;
        let j:number = 0;
        let duration:number = 0;
        timeline.add(() => {
            this._burst.show();
            this._burst.alpha = 1;
        });
        timeline.add(this._burst.getAnimation());
        sprites.left!.alpha = 0;
        if(frames.left) {
            const tlLeft:TimelineLite = new TimelineLite();
            const animationFramesL = isAborted ? this._END_SHORT_ANIMATION_FRAMES[j] : this._END_ANIMATION_FRAMES[j];
            frame = frames.left.stay;
            sprites.left!.texture = GameResources.getTextures(textures.left)[0];
            sprites.left!.x = frame.x!;
            sprites.left!.y = frame.y!;
            for(i = 0; i < animationFramesL.length; i++) {
                duration = i > 0 ? (animationFramesL[i].frame - animationFramesL[i - 1].frame) * fs : 0;
                tlLeft.to(sprites.left!, duration, {alpha : animationFramesL[i].alpha});
            }
            j++;
            timeline.add(tlLeft, '0');
        }
        sprites.right!.alpha = 0;
        if(frames.right) {
            const tlRight:TimelineLite = new TimelineLite();
            const animationFramesR = isAborted ? this._END_SHORT_ANIMATION_FRAMES[j] : this._END_ANIMATION_FRAMES[j];
            frame = frames.right.stay;
            sprites.right!.texture = GameResources.getTextures(textures.right)[0];
            sprites.right!.x = frame.x!;
            sprites.right!.y = frame.y!;
            for(i = 0; i < animationFramesR.length; i++) {
                duration = i > 0 ? (animationFramesR[i].frame - animationFramesR[i - 1].frame) * fs : 0;
                tlRight.to(sprites.right!, duration, {alpha : animationFramesR[i].alpha});
            }
            j++;
            timeline.add(tlRight, '0');
        }
        sprites.center!.alpha = 0;
        if(frames.center) {
            const tlCenter:TimelineLite = new TimelineLite();
            const animationFramesC = isAborted ? this._END_SHORT_ANIMATION_FRAMES[j] : this._END_ANIMATION_FRAMES[j];
            frame = frames.center.stay;
            sprites.center!.texture = GameResources.getTextures(textures.center!)[0];
            sprites.center!.x = frame.x!;
            sprites.center!.y = frame.y!;
            sprites.center!.alpha = 1;
            for(i = 0; i < animationFramesC.length; i++) {
                duration = i > 0 ? (animationFramesC[i].frame - animationFramesC[i - 1].frame) * fs : 0;
                tlCenter.to(sprites.center!, duration, {alpha : animationFramesC[i].alpha});
            }
            timeline.add(tlCenter, '0');
        }
        this._nonStopAnimationContainer.animateNeon(0);
        let me = this;
        timeline.eventCallback('onComplete', function() {
            me.removeEndAnimation();
        });
    }

    public stopWinPresentation(isAborted:boolean) {
        if(this._masterTimeLine && this._masterTimeLine.isActive()) {
            if(isAborted) {
                this._masterTimeLine.clear();
            }
            this._masterTimeLine.pause();
            this._masterTimeLine.kill();
            this._masterTimeLine = null;
        }
        this.removeAnimationSprites();
        this.removeOverlaySprites();
        this._nonStopAnimationContainer.stopAnimation();
        this._burst.hide();
        this._whiteBarAnimation.stopAnimation();
    }

    private removeOverlaySprites():void {
        if(this._animationOverlaySprites.left) {
            this._animationOverlaySprites.left.alpha = 0;
        }
        if(this._animationOverlaySprites.right) {
            this._animationOverlaySprites.right.alpha = 0;
        }
        if(this._animationOverlaySprites.center) {
            this._animationOverlaySprites.center.alpha = 0;
        }
    }

    private removeAnimationSprites():void {
        if(this._animationSprites.left) {
            this._animationSprites.left.alpha = 0;
        }
        if(this._animationSprites.right) {
            this._animationSprites.right.alpha = 0;
        }
        if(this._animationSprites.center) {
            this._animationSprites.center.alpha = 0;
        }
    }

    private showWinAmount():void {
        if(this._gameMode === GameMode.RESPIN) {
            this._winNumber.text = Currency.format(this._singleWin);
            this._winNumber.x = WixxGameConfig.instance.GAME_WIDTH / 2;
            this._winNumber.alpha = 1;
            this._layer.addChild(this._winNumber);
            TweenLite.fromTo(this._winNumber, 0.2, {alpha : 1}, {alpha : 0, delay : 0.4});
        }
    }

    private removeEndAnimation():void {
        this.removeAnimationSprites();
        this._nonStopAnimationContainer.reset();
        this._winNumber.alpha = 0;
        this._burst.hide();
        this._layer.removeChild(this._wixxAnimationContainer);
        this._layer.removeChild(this._winNumber);
    }

    public resetFeature():void {
        this._currentLevel = -1;
        this._winLevel = -1;
    }
}